/* eslint-disable complexity */
import { useAppForm } from '@/components/form';
import { useCalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { UpdateFormValidationSchema } from '@/domain/termine/features/UpdateTermin/shared/UpdateFormValidationSchema';
import { dateMapper } from '@/domain/termine/features/UpdateTermin/shared/utils/formMappers';
import { useUpdateTermin } from '@/domain/termine/hooks/useUpdateTermin';
import {
  TerminWiederholung,
  TerminWiederholungsIntervall,
} from '@/domain/termine/shared/types';
import { Termin } from '@/domain/termine/shared/types/Termin';
import {
  TerminUpdateImageInput,
  UpdateFormValueInput,
} from '@/domain/termine/shared/types/UpdateFormValueInput';
import { getWeekdaySelection } from '@/domain/termine/shared/utils/getWeekdaySelection';
import { fileToBase64 } from '@/domain/termine/shared/utils/imageProcessing';
import { useToast } from '@/hooks/use-toast';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import z from 'zod';

/* eslint-disable max-lines-per-function */
export function useUpdateTerminForm(termin?: Termin) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateTermin } = useUpdateTermin();
  const { toast } = useToast();
  const { standortId } = useCalendarContext();
  const location = useLocation();

  function formValuesIntoTerminUpdate(
    data: UpdateFormValueInput,
  ): UpdateFormValueInput {
    const mapped = dateMapper(data);
    return {
      id: data.id,
      titel: data.titel,
      beschreibung: data.beschreibung || '',
      datum: mapped.dateOnly,
      beginn: mapped.beginnDateTime || '',
      ende: mapped.endeDateTime || '',
      specials: data.specials || '',
      markierung: data.markierung,
      wiederholung: data.wiederholung || TerminWiederholung.OHNE,
      wiederholungsIntervall:
        data?.wiederholungsIntervall ?? TerminWiederholungsIntervall.WOCHE,
      wochentagAuswahl: getWeekdaySelection(data),
      occurrenceCount: data?.occurrenceCount ?? undefined,
      wochentagAuftreten: data?.wochentagAuftreten || undefined,
      wiederholungBis: mapped.wiederholungBisTime ?? '',
      terminBild: data.terminBild ?? undefined,
      terminKalenderId: data.terminKalenderId,
    };
  }

  const datum = location?.state?.datum ?? termin?.datum ?? '';
  return useAppForm({
    defaultValues: {
      id: termin?.id ?? '',
      titel: termin?.titel ?? '',
      beschreibung: termin?.beschreibung ?? '',
      datum,
      beginn: termin?.beginn ? dayjs(termin.beginn).format('HH:mm') : undefined,
      ende: termin?.ende ? dayjs(termin.ende).format('HH:mm') : undefined,
      markierung: termin?.markierung ?? 'GRAY',
      specials: termin?.specials,
      wiederholung: termin?.wiederholung ?? TerminWiederholung.OHNE,
      wiederholungsIntervall:
        termin?.individuelleWiederholung?.wiederholungsIntervall ??
        TerminWiederholungsIntervall.WOCHE,
      occurrenceCount: termin?.individuelleWiederholung?.occurrenceCount ?? 1,
      wochentagAuftreten:
        termin?.individuelleWiederholung?.weekDayOccurrence ?? undefined,
      wochentagAuswahl: termin?.individuelleWiederholung?.wochentage,
      wiederholungBis: termin?.wiederholungBis
        ? dayjs(termin.wiederholungBis).format('YYYY-MM-DD')
        : '',
      terminBild: termin?.image
        ? {
            contentType: termin.image.contentType ?? '',
            url: termin.image.url ?? '',
            thumbnailUrl: termin.image.thumbnailUrl ?? '',
            name: termin.image?.name ?? '',
            file: termin.image.file,
          }
        : undefined,
      terminKalenderId: termin?.idTerminkalender ?? 'NO_KALENDER',
    } satisfies z.infer<
      ReturnType<typeof UpdateFormValidationSchema>
    > as z.infer<ReturnType<typeof UpdateFormValidationSchema>>,
    validators: {
      onBlur: UpdateFormValidationSchema(),
    },
    onSubmit: async ({ value }) => {
      if (!termin) return;
      const terminBild = value.terminBild;
      const selectedFile = terminBild?.file;
      let terminBildInput: TerminUpdateImageInput | undefined;

      if (terminBild === null) {
        terminBildInput = { name: null, base64Image: null, idStandort: null };
        // DELETE terminBild
      } else if (selectedFile instanceof File) {
        if (!standortId) {
          throw new Error(t('domain.termine.form.keinStandort'));
        }
        const base64Data = await fileToBase64(selectedFile);
        terminBildInput = {
          name: terminBild?.name || selectedFile.name,
          base64Image: base64Data,
          idStandort: standortId,
        };
        //UPDATE terminImage
      } else {
        terminBildInput = undefined;
        //KEEP terminImage
      }

      const terminInput = {
        ...formValuesIntoTerminUpdate(value),
        terminBildInput,
      };

      const response = await updateTermin(terminInput);

      if (!response) {
        throw Error('No response from updateTermin mutation');
      }

      if (response.error) {
        return toast({
          title: t('domain.termine.form.messages.terminBearbeiten.error.titel'),
          description:
            response.error.message ||
            t(
              'domain.termine.form.messages.terminBearbeiten.error.description',
            ),
          variant: 'destructive',
        });
      }

      toast({
        title: t('domain.termine.form.messages.terminBearbeiten.titel'),
      });

      const terminDate = dayjs(value.datum);
      const terminYear = terminDate.format('YYYY');
      const terminMonth = terminDate.format('M');
      const terminDay = terminDate.format('D');
      navigate(`/termine/uebersicht/${terminYear}/${terminMonth}/${terminDay}`);
    },
  });
}
